// 📁 src/pages/MVPPlanner.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "../utils/axiosInstance";
import { useIdea } from "../context/IdeaContext";
import { useNavigate, useLocation } from "react-router-dom";
import IdeaPopup from "./IdeaPopup";
import { PlayCircle,CheckCircle,Download,ArrowRight,Sparkles } from "lucide-react";
import buildMDIHPdfName from "../utils/pdfNaming";
import ModuleHeader from "../components/ModuleHeader";

const MVPPlanner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedIdea, markModuleComplete, mvpDetails, setMvpDetails, businessModel } =
    useIdea();
  
    /* RESOLVE STARTUP NAME (CONSISTENT WITH PLATFORM) */

    const state = location?.state || {};

    const ideaTitle =

    selectedIdea?.ideaTitle ||

    selectedIdea?.title ||

    state?.ideaTitle ||

    localStorage.getItem("activeIdeaTitle") ||

    businessModel?.startupName ||

    mvpDetails?.goal ||

    "Startup Idea";


    const startupName = ideaTitle;

  const ideaId =

    location?.state?.ideaId ||

    selectedIdea?.ideaId ||

    localStorage.getItem("activeIdeaId") ||

    null;

  const ideaText =
    location?.state?.ideaText ||
    selectedIdea?.title ||
    selectedIdea?.ideaTitle ||
    selectedIdea?.idea ||
    "";
    // selectedIdea?.problemStatement || //
    // selectedIdea?.description || //
    
    
  const mvpRecordId =
    location?.state?.mvpRecordId || mvpDetails?.recordId || null;

  const [formData, setFormData] = useState({
    ideaTitle: "",
    problem: "",
    targetUser: "",
    solution: "",
    coreFeatures: [],
  });

  const [manualOverride, setManualOverride] = useState(false);
  const [plan, setPlan] = useState([]);
  const [currency, setCurrency] = useState("INR");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [completedSteps, setCompletedSteps] = useState({});
  const [autoSaved, setAutoSaved] = useState(false);
  const saveTimeoutRef = useRef(null);

  const [showPopup, setShowPopup] = useState(false);
  const [popupPayload, setPopupPayload] = useState(null);

  // Auto-fill
  useEffect(() => {
    if (selectedIdea && !manualOverride) {
      setFormData({
        ideaTitle:
        selectedIdea.title ||
        selectedIdea.ideaTitle ||
        "",
        problem: selectedIdea.problem || "",
        targetUser: selectedIdea.targetUser || "",
        solution: selectedIdea.solution || "",
        coreFeatures:
          selectedIdea.coreFeatures ||
          (selectedIdea.solution
            ? selectedIdea.solution.split(",")
            : []),
      });
    }
  }, [selectedIdea, manualOverride]);

  useEffect(() => {
    if (mvpDetails && mvpDetails.steps) {
      setPlan(mvpDetails.steps);

      const map = {};
      mvpDetails.steps.forEach((s) => {
        if (s.status === "Completed") map[s.stepNumber] = true;
      });
      setCompletedSteps(map);

      if (mvpDetails.currency) setCurrency(mvpDetails.currency);
      return;
    }

    (async () => {
      try {
        const res = await axios.get("/api/mvp-planner/last-plan", {
          params: { ideaId },
        });

        if (res.data?.success && res.data.mvpDetails?.steps) {
          setPlan(res.data.mvpDetails.steps);
          setMvpDetails(res.data.mvpDetails);

          if (res.data.mvpDetails.currency) {
            setCurrency(res.data.mvpDetails.currency);
          }
        }
      } catch (err) {
        if (err.response?.status === 401) navigate("/login");
      }
    })();
  }, []);

  const showMotivation = async (context = "") => {
    try {
      const res = await axios.post("/api/mvp-planner/ai-tip", {
        module: "MVP Planner",
        idea: ideaText,
        ideaId,
        recordId: mvpRecordId,
        context,
      });

      const tip =
        res.data?.tip || "You're doing great — keep it up!";

      setPopupPayload({
        idea: { title: "MVP Planner", description: context },
        type: "feedback",
        aiMessageOverride: tip,
      });

      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3500);
    } catch (err) {
      console.error("❌ AI tip error:", err);
    }
  };

  const fetchPlan = async () => {
    setLoading(true);
    setError("");

    try {
      const payload = {
        ...formData,
        currency,
        ideaId,
        ideaText,
        recordId: mvpRecordId,
      };

      const res = await axios.post("/api/mvp-planner/generate-plan", payload);

      const newPlan = res.data?.steps || [];
      setPlan(newPlan);

      const data = {
        recordId: res.data?.recordId || mvpRecordId,
        goal: formData.solution || formData.ideaTitle,
        steps: newPlan,
        currency,
      };

      setMvpDetails(data);
      markModuleComplete("mvp", data);

      localStorage.setItem("latestMVPPlan", JSON.stringify({ ...data, formData }));
      showMotivation("Your MVP implementation plan is ready!");
    } catch (err) {
      setError("Failed to generate plan");
    } finally {
      setLoading(false);
    }
  };

  const autoSave = (updatedSteps) => {
    if (!Array.isArray(updatedSteps)) return;

    if (saveTimeoutRef.current)
      clearTimeout(saveTimeoutRef.current);

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await axios.post("/api/mvp-planner/update-plan", {
          ideaId,
          recordId: mvpRecordId,
          data: { ...formData, steps: updatedSteps, currency },
        });

        setAutoSaved(true);
        setTimeout(() => setAutoSaved(false), 1400);
        showMotivation("Your plan was saved!");
      } catch (err) {
        console.error("❌ Auto-save failed:", err);
      }
    }, 900);
  };

  const updateProgress = async (stepNumber, isChecked) => {
    try {
      await axios.post("/api/mvp-planner/update-progress", {
        ideaId,
        recordId: mvpRecordId,
        stepNumber,
        status: isChecked ? "Completed" : "Not Started",
      });

      setCompletedSteps((prev) => ({ ...prev, [stepNumber]: isChecked }));

      const updated = plan.map((s) =>
        Number(s.stepNumber) === Number(stepNumber)
          ? { ...s, status: isChecked ? "Completed" : "Not Started" }
          : s
      );

      setPlan(updated);
      autoSave(updated);

      if (isChecked)
        showMotivation(`Nice! Step ${stepNumber} completed.`);
    } catch (err) {
      console.error("❌ Progress error:", err);
    }
  };

  const downloadPDF = async () => {

  if (!plan?.length) return;

  try {

    const token = localStorage.getItem("token");

    const res = await axios.post(
      "/api/pdf/mvp-planner",
      { ideaId },
      {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: "blob"
      }
    );

    const url = window.URL.createObjectURL(
      new Blob([res.data], { type: "application/pdf" })
    );

    const link = document.createElement("a");
    link.href = url;
    link.download =
      buildMDIHPdfName(
      startupName,
      "MVP_Roadmap"
      );

    document.body.appendChild(link);
    link.click();
    link.remove();

  } catch (err) {

    console.error("❌ PDF download failed:", err);

  }

};

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-6xl mx-auto px-6 py-10 space-y-10 relative">

        {autoSaved && (
          <div className="text-green-600 flex items-center gap-2 text-sm">
            <CheckCircle size={16} /> Auto-saved
          </div>
        )}

        {/* MODULE HEADER */}

        <ModuleHeader

        title="MVP Implementation Planner"

        subtitle="Convert your validated idea into structured execution steps. Define scope, time, cost, and learning resources before building."

        currentModule="mvp"

        ideaTitle={startupName}

        />

        {/* ACTIVE IDEA */}

        <div className="text-sm text-gray-500 border-b border-slate-200 pb-4">

        <b>Startup:</b> {startupName}

        </div>

        {/* FORM */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 space-y-6">

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={manualOverride}
              onChange={() => setManualOverride((p) => !p)}
            />
            <span className="text-sm text-slate-700">
              ✍️ Manually edit fields
            </span>
          </div>

          <div className="grid gap-6">

            <div>
              <label className="block text-base font-semibold text-blue-900 mb-2">
                Idea Title
              </label>
              <input
                name="ideaTitle"
                disabled={!manualOverride}
                value={formData.ideaTitle}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, ideaTitle: e.target.value }))
                }
                className="w-full border border-slate-300 rounded-lg px-4 py-3 text-base"
              />
            </div>

            <div>
              <label className="block text-base font-semibold text-blue-900 mb-2">
                Problem Statement
              </label>
              <textarea
                name="problem"
                disabled={!manualOverride}
                value={formData.problem}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, problem: e.target.value }))
                }
                className="w-full border border-slate-300 rounded-lg px-4 py-3 text-base min-h-[110px]"
              />
            </div>

            <div>
              <label className="block text-base font-semibold text-blue-900 mb-2">
                Target User
              </label>
              <input
                name="targetUser"
                disabled={!manualOverride}
                value={formData.targetUser}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, targetUser: e.target.value }))
                }
                className="w-full border border-slate-300 rounded-lg px-4 py-3 text-base"
              />
            </div>

            <div>
              <label className="block text-base font-semibold text-blue-900 mb-2">
                Proposed Solution
              </label>
              <textarea
                name="solution"
                disabled={!manualOverride}
                value={formData.solution}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, solution: e.target.value }))
                }
                className="w-full border border-slate-300 rounded-lg px-4 py-3 text-base min-h-[110px]"
              />
            </div>

            <div>
              <label className="block text-base font-semibold text-blue-900 mb-2">
                Core MVP Features
              </label>
              <input
                name="coreFeatures"
                disabled={!manualOverride}
                value={
                  Array.isArray(formData.coreFeatures)
                    ? formData.coreFeatures.join(", ")
                    : ""
                }
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    coreFeatures: e.target.value
                      .split(",")
                      .map((s) => s.trim()),
                  }))
                }
                className="w-full border border-slate-300 rounded-lg px-4 py-3 text-base"
              />
            </div>
          </div>

          <div className="flex items-center gap-4 pt-4">
            <label className="text-base font-medium text-slate-700">
              Preferred Currency:
            </label>

            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="border border-slate-300 rounded-lg px-4 py-2 text-base"
            >
              <option value="USD">USD ($)</option>
              <option value="INR">INR (₹)</option>
              <option value="EUR">EUR (€)</option>
            </select>

            <button
              onClick={fetchPlan}
              disabled={loading}
              className="ml-auto bg-blue-600 text-white px-7 py-3 rounded-lg text-base font-semibold hover:bg-blue-700 transition"
            >
              {loading ? "Thinking…" : "Generate Implementation Plan"}
            </button>
          </div>

          {error && <p className="text-red-600 text-sm">{error}</p>}
        </div>

        {/* PLAN SECTION */}
        {plan.length > 0 && (
          <div className="space-y-8">

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-semibold text-blue-900">
                  Your MVP Plan
                </h3>
                <button
                  onClick={downloadPDF}
                  className="mt-4 bg-green-600 hover:bg-blue-700
                          text-white px-6 py-3 rounded-lg font-semibold
                          inline-flex items-center gap-2 transition"
              >
                <Download size={18} />
                
                  📄 Download MVP Roadmap
                </button>
              </div>

              <table className="w-full border border-slate-200 text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="p-3 border">Step #</th>
                    <th className="p-3 border">Title</th>
                    <th className="p-3 border">Module</th>
                    <th className="p-3 border">Hours</th>
                    <th className="p-3 border">Cost ({currency})</th>
                    <th className="p-3 border">Done</th>
                    <th className="p-3 border">Resources</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.map((step) => (
                    <tr key={step.stepNumber}>
                      <td className="border p-3">{step.stepNumber}</td>
                      <td className="border p-3">{step.title}</td>
                      <td className="border p-3">{step.recommendedModule}</td>
                      <td className="border p-3">{step.estimatedHours || "-"}</td>
                      <td className="border p-3">
                        {step.estimatedCost
                          ? `${step.estimatedCost} ${currency}`
                          : "-"}
                      </td>
                      <td className="border p-3 text-center">
                        <input
                          type="checkbox"
                          checked={!!completedSteps[step.stepNumber]}
                          onChange={(e) =>
                            updateProgress(step.stepNumber, e.target.checked)
                          }
                        />
                      </td>
                      <td className="border p-3">
                        {Array.isArray(step.externalResources) &&
                        step.externalResources.length ? (
                          step.externalResources.map((r, i) => (
                            <a
                              key={i}
                              href={r.url}
                              target="_blank"
                              rel="noreferrer"
                              className="block text-blue-600 underline"
                            >
                              🌐 {r.name}
                            </a>
                          ))
                        ) : (
                          <span className="text-gray-500 italic">
                            No AI resources yet
                          </span>
                        )}

                        {step.internalLink && (
                          <a
                            href={step.internalLink}
                            className="text-green-600 underline block"
                          >
                            🎓 Learn More in {step.recommendedModule}
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* POST PLAN PRIMARY CTA BLOCK */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 space-y-4 text-center">
              <h3 className="text-xl font-semibold text-blue-900">
                Ready to Move Forward?
              </h3>

              <p className="text-slate-600 text-sm">
                Now deepen execution planning with AI guidance or structure your first business model.
              </p>

              <div className="flex justify-center gap-6 pt-3">
                <button
                  onClick={() =>
                    navigate("/mvp-agent", {
                      state: { ideaId, ideaText, mvpRecordId },
                    })
                  }
                  className="bg-blue-700 text-white px-7 py-3 rounded-lg text-base font-semibold hover:bg-blue-800 transition"
                >
                  🚀 MVP Execution Plan (Detailed Plan)
                </button>

                <button
                  onClick={() =>
                    navigate("/business-model-builder", {
                      state: { ideaId, ideaText },
                    })
                  }
                  className="bg-purple-700 text-white px-7 py-3 rounded-lg text-base font-semibold hover:bg-purple-800 transition"
                >
                  📊 Build First Business Model
                </button>
              </div>
            </div>

          </div>
        )}

        {showPopup && popupPayload && (
          <IdeaPopup
            idea={popupPayload.idea}
            type={popupPayload.type}
            aiMessageOverride={popupPayload.aiMessageOverride}
            onClose={() => setShowPopup(false)}
            autoClose={true}
            autoCloseDelay={3500}
            showCloseButton={true}
          />
        )}

      </div>
    </div>
  );
};

export default MVPPlanner;
